import type {
  BenefitSelectionChild,
  CoverageSchemaResponse,
} from '@/features/quotes/types/group-life/coverage-schema.types';
import type { GroupLifeFrontCoverageSelection } from '@/services/group-life';
import { firstMeaningfulValue } from './coverage-unit-premium.util';

/**
 * Capital das coberturas de SELEÇÃO DE BENEFÍCIO, lido do schema do produto.
 *
 * Nos auxílios o capital é intrínseco à opção escolhida (5k/7k/10k vêm do
 * catálogo) e por isso NÃO é persistido na cotação: o InsureMO tarifa pelo
 * código do benefício, e `plan.coverageSumInsured` só ganha o item depois do
 * cálculo — antes disso a tela não teria de onde tirar o número. A IS fixa
 * chega no schema, em `children[].defaultSumInsured` (o `DefaultSI` do
 * InsureMO) — é onde o produto configura o 5k/7k/10k. O `sumInsured` do
 * benefício vem vazio no 930016 e fica só como reserva.
 *
 * Fica de fora o `BENEFIT_SELECTION_WITH_SI`: ali o capital é DIGITADO pelo
 * corretor com base na opção, então o valor do catálogo seria um palpite.
 *
 * O mapa é indexado por CÓDIGO e por NOME porque o demonstrativo do cálculo
 * traz ora um, ora outro.
 */
export function benefitSumInsuredByCoverage(
  schema: CoverageSchemaResponse | undefined,
  selections: Record<string, GroupLifeFrontCoverageSelection> | undefined
): Map<string, number> {
  const values = new Map<string, number>();
  if (!schema || !selections) return values;

  for (const coverage of schema.coverages) {
    if (coverage.sumInsuredDefinition !== 'BENEFIT_SELECTION') continue;
    const selection = selections[coverage.code];
    if (!selection?.enabled || selection.kind !== 'BENEFIT_SELECTION') continue;

    const child = coverage.children.find(
      item => item.code === selection.selectedChildCoverageCode
    );
    const sumInsured = benefitSumInsured(child);
    if (sumInsured == null) continue;

    for (const key of [coverage.code, coverage.name]) {
      values.set(key, sumInsured);
    }
  }

  return values;
}

/**
 * Rótulo de uma opção de benefício no select: `nome — valor`, SEM o valor
 * quando o nome já o mostra. Nos auxílios e assistências funeral o nome da
 * opção É o valor ("R$ 5.000"), e juntar o `DefaultSI` duplicava
 * ("R$ 5.000 — R$ 5.000,00").
 */
export function benefitOptionLabel(
  name: string,
  sumInsured: number | undefined,
  formatCurrency: (value: number) => string
): string {
  if (sumInsured == null || nameShowsAmount(name, sumInsured)) return name;
  return `${name} — ${formatCurrency(sumInsured)}`;
}

/** Algum valor em formato pt-BR no nome ("5.000", "7.000,00") é o próprio `amount`. */
function nameShowsAmount(name: string, amount: number): boolean {
  const amountsInName = name.match(/\d{1,3}(?:\.\d{3})+(?:,\d+)?|\d+(?:,\d+)?/g) ?? [];
  return amountsInName.some(
    text => Number(text.replace(/\./g, '').replace(',', '.')) === amount
  );
}

/**
 * IS de um benefício: `DefaultSI` primeiro, `SumInsured` como reserva.
 *
 * `firstMeaningfulValue` e não `??`: o campo que não se aplica chega ZERO, não
 * nulo, e um `??` pararia nele.
 */
export function benefitSumInsured(
  child: BenefitSelectionChild | undefined
): number | undefined {
  if (!child) return undefined;
  return firstMeaningfulValue(child.defaultSumInsured, child.sumInsured);
}
